<template>
  <div class="root">
    <div class="main-container" :style="mainPreviewBackgroundStyle">
      <AvatarBuilderUI />
    </div>
    <div class="bottom-safe-area-inset" :style="bottomSafeAreaStyle" />
    <!-- Absolutely positioned dialogs/popups -->
    <ExitDialog v-if="shouldShowExitDialog" @cancel="onExitDialogCancel" @exit="onExitDialogExit" />
    <OutfitDialog
      v-if="shouldShowOutfitDialog"
      @cancel="onOutfitDialogCancel"
      @agree="onOutfitDialogAgree"
    />
    <DeleteSelfiePreviewDialog
      v-if="shouldShowDeleteSelfieDialog"
      @cancel="onDeleteSelfieDialogCancel"
      @delete="onDeleteSelfieDialogDelete"
    />
    <UnsupportedOSDialog v-if="shouldShowUnsupportedOSError" @exit="onExitDialogExit" />
    <ItemUnavailableDialog v-if="shouldShowItemUnavailableDialog" @ok="onItemUnavailableDialogOk" />
    <ItemDeprecatedDialog v-if="shouldShowItemDeprecatedDialog" @ok="onItemDeprecatedDialogOk" />
  </div>
</template>

<script>
import AvatarBuilderUI from './AvatarBuilderUI.vue';
import ExitDialog from '@/components/AvatarBuilderUI/Dialogs/ExitDialog.vue';
import OutfitDialog from '@/components/AvatarBuilderUI/Dialogs/OutfitDialog.vue';
import ItemUnavailableDialog from '@/components/AvatarBuilderUI/Dialogs/ItemUnavailableDialog.vue';
import ItemDeprecatedDialog from '@/components/AvatarBuilderUI/Dialogs/ItemDeprecatedDialog.vue';
import DeleteSelfiePreviewDialog from '@/components/AvatarBuilderUI/Dialogs/DeleteSelfiePreviewDialog.vue';
import ClientBuild from '@/services/ClientBuild';
import ReceiveClientMessage from '@/services/ReceiveClientMessage';
import { OUTFIT_CATEGORY_KEY } from '@/constants';
import { SaveFailureReasonEnum } from '@/enum/save_failure_reason';
import { ExitDialogTypeEnum } from '@/enum/exit_dialog_type';
import { OutfitSharingStateEnum } from '@/enum/outfit_sharing_state';

export default {
  name: 'RootUI',
  components: {
    AvatarBuilderUI,
    DeleteSelfiePreviewDialog,
    ExitDialog,
    OutfitDialog,
    ItemUnavailableDialog,
    ItemDeprecatedDialog,
  },
  computed: {
    shouldShowDeleteSelfieDialog() {
      return this.$store.getters.shouldShowDeleteSelfieDialog;
    },
    shouldShowExitDialog() {
      return this.$store.getters.shouldShowExitDialog;
    },
    shouldShowOutfitDialog() {
      return this.$store.getters.shouldShowOutfitDialog;
    },
    shouldShowUnsupportedOSError() {
      return this.$store.getters.shouldShowUnsupportedOSError;
    },
    shouldShowItemUnavailableDialog() {
      return this.$store.getters.shouldShowItemUnavailableDialog;
    },
    shouldShowItemDeprecatedDialog() {
      return this.$store.getters.outfitSharingState === OutfitSharingStateEnum.OUTFIT_DEPRECATED;
    },
    mainPreviewBackgroundStyle() {
      return {
        height: `calc(100% - ${ClientBuild.getClientInsets().bottom}px)`,
      };
    },
    bottomSafeAreaStyle() {
      return {
        height: `${ClientBuild.getClientInsets().bottom}px`,
      };
    },
  },
  mounted: function() {
    ReceiveClientMessage.BackButtonEventBus.$on('pressed', this.onClientBackButtonPress);
    ReceiveClientMessage.SaveFailedEventBus.$on('failed', this.onSaveFailed);
  },
  methods: {
    onExitDialogOpen: function() {
      this.$store.commit('setShouldShowExitDialogIfChanges', { show: true });
      if (!this.$store.getters.shouldShowExitDialog) {
        this.$store.dispatch('exitBuilder');
      }
    },
    onExitDialogCancel: function() {
      this.$store.commit('setShouldShowExitDialogIfChanges', { show: false });
    },
    onExitDialogExit: function() {
      this.$store.commit('setShouldShowExitDialogIfChanges', { show: false });
      this.$store.dispatch('exitBuilder');
    },
    onOutfitDialogCancel: function() {
      this.$store.commit('setShouldShowOutfitDialog', { show: false });
    },
    onOutfitDialogAgree: function() {
      this.$store.commit('setShouldShowOutfitDialog', { show: false });
      this.$store.commit('scrollToPageWithCategoryKey', { categoryKey: OUTFIT_CATEGORY_KEY });
    },
    onClientBackButtonPress: function() {
      // The Android back button was pressed.
      if (this.shouldShowUnsupportedOSError) {
        // "showUnsupportedOSError" does not have any cancel action so exit
        this.onExitDialogExit();
      } else if (this.shouldShowOutfitDialog) {
        this.onOutfitDialogCancel();
      } else if (this.shouldShowExitDialog) {
        this.onExitDialogCancel();
      } else {
        // No dialog or window is open
        this.onExitDialogOpen();
      }
    },
    onDeleteSelfieDialogCancel: function() {
      this.$store.commit('setShouldShowDeleteSelfieDialog', { show: false });
    },
    onDeleteSelfieDialogDelete: function() {
      this.$store.commit('setShouldShowDeleteSelfieDialog', { show: false });
      this.$store.commit('setIsSelfiePreviewDismissed', { isDismissed: true });
    },
    onSaveFailed: function(failureReason) {
      if (failureReason == SaveFailureReasonEnum.ITEM_UNAVAILABLE) {
        this.$store.commit('setShouldShowItemUnavailableDialog', { show: true });
      }
    },
    onItemUnavailableDialogOk: function() {
      this.$store.commit('setShouldShowItemUnavailableDialog', { show: false });
      // After the user has seem the item unavailable dialog, they should not see the Unlockable exit dialog as well.
      this.$store.commit('setExitDialogType', { type: ExitDialogTypeEnum.DEFAULT });
    },
    onItemDeprecatedDialogOk: function() {
      this.$store.commit('setOutfitSharingState', {
        outfitSharingState: OutfitSharingStateEnum.NOT_LOADING,
      });
    },
  },
};
</script>

<style scoped>
.root {
  height: 100%;
  overflow: hidden;

  /*
    Support hardware acceleration:
    https://stackoverflow.com/a/18529444
   */
  -webkit-transform: translateZ(0); /* Safari */
  transform: translateZ(0);
}

.main-container {
  overflow: hidden;
  position: absolute;
  width: 100%;
}
/*
@media screen and (aspect-ratio: 9/16) {
  .main-preview-background.full-body {
    top: -18%;
  }
} */

@media screen and (aspect-ratio: 9/16),
  screen and (width: 375px) and (height: 667px),
  screen and (width: 414px) and (height: 736px),
  screen and (width: 360px) and (height: 640px) {
  .main-preview-background.full-body {
    top: -18%;
  }
}

/* The bottom inset for tall iPhones is filled with a white view at the bottom of the screen.
  If the traits tab bar colour changes, this colour needs to change too. */
.bottom-safe-area-inset {
  position: absolute;
  background-color: white;
  bottom: 0;
  width: 100%;
}

@media (prefers-color-scheme: dark) {
  .bottom-safe-area-inset {
    background-color: #141414;
  }
}
</style>
